<template>
  <gs-named-modal
    name="projections-modal"
    :title="$t('frontend_groovesell_menu_items_projections')"
    width="min(100%, 880px)"
    max-width="880px"
    class="gs-projections"
  >
    <div class="gs-aff-panel">
      <p class="gs-projections__intro">
        Enter how much you expect to keep from one sale of each price point. GrooveSell uses these numbers for funnel projections.
      </p>

      <div v-if="pricepointsForProjections.length > 6" class="gs-aff-panel__meta">
        <label class="gs-aff-search">
          <gs-icon name="search" size="16"></gs-icon>
          <input v-model="search" type="search" placeholder="Search price points" aria-label="Search price points" />
        </label>
        <span class="gs-aff-count">{{ projectedCount }} of {{ pricepointsForProjections.length }} price points have projections</span>
      </div>

      <div class="gs-aff-list gs-projections__list">
        <article
          v-for="{ pricePoint, index } in visiblePricePoints"
          :key="pricePoint.id || index"
          class="gs-aff-row"
          :class="{ 'is-open': openIndex === index, 'is-configured': hasProjection(pricePoint) }"
        >
          <button
            type="button"
            class="gs-aff-row__head"
            :aria-expanded="openIndex === index ? 'true' : 'false'"
            @click="toggle(index)"
          >
            <span class="gs-aff-row__dot" aria-hidden="true"></span>
            <span class="gs-aff-row__title">
              <span class="gs-aff-row__name">{{ pricePoint.name }}</span>
              <span class="gs-aff-row__summary">{{ priceStory(pricePoint) }}</span>
            </span>
            <span class="gs-aff-row__meta">
              <span v-if="hasProjection(pricePoint)" class="gs-aff-pill gs-aff-pill--success">
                Keeps {{ money(pricePoint.projected_after_aff_refunds) }}
              </span>
              <span v-else class="gs-aff-pill">Not set</span>
            </span>
            <span class="gs-aff-row__chevron" aria-hidden="true">
              <gs-icon :name="openIndex === index ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
            </span>
          </button>

          <div v-if="openIndex === index" class="gs-aff-row__body">
            <div class="gs-projections__fields">
              <label class="gs-aff-field">
                <span class="gs-aff-field__label">After all payments</span>
                <gd-currency-input v-model="pricePoint.projected" :currency-code="currencyCode" />
                <span class="gs-aff-field__hint">Total from one buyer once every payment is collected.</span>
              </label>
              <label class="gs-aff-field">
                <span class="gs-aff-field__label">After affiliate commissions</span>
                <gd-currency-input v-model="pricePoint.projected_after_aff" :currency-code="currencyCode" />
                <span class="gs-aff-field__hint">What is left after paying affiliates.</span>
              </label>
              <label class="gs-aff-field">
                <span class="gs-aff-field__label">After commissions and refunds</span>
                <gd-currency-input v-model="pricePoint.projected_after_aff_refunds" :currency-code="currencyCode" />
                <span class="gs-aff-field__hint">What you expect to keep in the end.</span>
              </label>
            </div>
          </div>
        </article>

        <div v-if="!visiblePricePoints.length" class="gs-aff-empty">
          <strong>No matching price points</strong>
          <p>Try a different search.</p>
        </div>
      </div>
    </div>

    <template slot="footer">
      <gs-button type="button" variant="outline" @click="close">{{ $t('frontend_cancel') }}</gs-button>
      <gs-button type="button" variant="primary" :loading="saving" @click="savePricePointProjections">
        {{ $t('frontend_save') }}
      </gs-button>
    </template>
  </gs-named-modal>
</template>

<script>
import { billingStory } from '../../pricePointSummary';
import '../../steps/tabs/affiliates/affiliate-settings.css';

export default {
  props: {
    pricepointsForProjections: {
      type: Array,
      required: true
    },
    funnel: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      openIndex: 0,
      search: '',
      saving: false,
    };
  },
  computed: {
    currencyCode() {
      return String(this.funnel.currency_code || 'USD').toUpperCase();
    },
    visiblePricePoints() {
      const term = this.search.trim().toLowerCase();
      return this.pricepointsForProjections
        .map((pricePoint, index) => ({ pricePoint, index }))
        .filter(({ pricePoint }) => !term || String(pricePoint.name || '').toLowerCase().includes(term));
    },
    projectedCount() {
      return this.pricepointsForProjections.filter(this.hasProjection).length;
    },
  },
  watch: {
    pricepointsForProjections() {
      // A different product was opened: start from its first price point.
      this.openIndex = 0;
      this.search = '';
    },
  },
  methods: {
    toggle(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
    hasProjection(pricePoint) {
      return Number(pricePoint.projected_after_aff_refunds) > 0;
    },
    money(value) {
      const amount = Number(value) || 0;
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currencyCode, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);
      } catch (error) {
        return `${this.funnel.currency_symbol || ''}${amount.toLocaleString('en-US')}`;
      }
    },
    priceStory(pricePoint) {
      return billingStory({
        type: pricePoint.type,
        price: pricePoint.price,
        trial_price: pricePoint.trial_price,
        is_trial: pricePoint.is_trial,
        trial_period: pricePoint.trial_period,
        trial_period_type: pricePoint.trial_period_type,
        recurring_interval: pricePoint.recurring_interval,
        recurrence_interval: pricePoint.recurrence_interval,
        number_of_installments: pricePoint.number_of_installments,
        currency_code: this.currencyCode,
        currency_symbol: this.funnel.currency_symbol,
      });
    },
    close() {
      this.$root.$emit('modal-close', 'projections-modal');
    },
    async savePricePointProjections() {
      this.saving = true;
      this.$loader.start('savePricePointProjections');
      try {
        await Promise.all(this.pricepointsForProjections.map((pricePoint) => pricePoint.save()));
        this.close();
      } finally {
        this.saving = false;
        this.$loader.end('savePricePointProjections');
      }
    },
  },
};
</script>

<style>
.gs-projections__intro {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.5;
}

/* The kit leaves .gs-modal__body unpadded and scrolling; inset it here and
   keep the actions visible while long price point lists scroll. */
.gs-projections .gs-modal__body {
  padding: 20px 24px 0;
}

.gs-projections .gs-named-modal__footer {
  position: sticky;
  bottom: 0;
  z-index: 1;
  margin: 20px -24px 0;
  padding: 14px 24px;
  background: var(--gs-surface);
  border-top: 1px solid var(--gs-border);
}

.gs-projections__fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 760px) {
  .gs-projections__fields {
    grid-template-columns: 1fr;
  }
}
</style>
